import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import API_BASE_URL from "./config";
import "bootstrap/dist/css/bootstrap.min.css";

export default function Login() {
  const [searchParams] = useSearchParams();
  const defaultLang = searchParams.get("lang") || localStorage.getItem("language") || "en";
  const [language, setLanguage] = useState(defaultLang);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Save language
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const text = {
    en: {
      title: "Umojai",
      subtitle: "Adaptive intelligence model for English and Kiswahili",
      placeholderEmail: "Enter your email",
      placeholderPassword: "Enter your password",
      loginButton: "Login",
      signupText: "Don't have an account?",
      signupLink: "Sign Up",
      forgotPassword: "Forgot Password?",
      errorMessage: "Invalid email or password. Please try again.",
      networkError: "Network error. Please try again later.",
    },
    sw: {
      title: "Umojai",
      subtitle: "Mfumo wa akili bandia inayobadilika kwa Kiingereza na Kiswahili",
      placeholderEmail: "Ingiza barua pepe yako",
      placeholderPassword: "Ingiza nenosiri lako",
      loginButton: "Ingia",
      signupText: "Huna akaunti?",
      signupLink: "Jisajili",
      forgotPassword: "Umesahau Nenosiri?",
      errorMessage: "Barua pepe au nenosiri si sahihi. Tafadhali jaribu tena.",
      networkError: "Tatizo la mtandao. Tafadhali jaribu tena baadaye.",
    },
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, language }),
      });

      const data = await response.json();
      console.log("🔍 Full Login API Response:", data);
      console.log("🔍 HTTP Status Code:", response.status);

      if (response.ok && data.token && data.name) {
        const firstName = data.name.includes(" ") ? data.name.split(" ")[0] : data.name;

        localStorage.setItem("token", data.token);
        localStorage.setItem("name", firstName);
        localStorage.setItem("language", language);

        const chatbotPage = data.redirect || (language === "sw" ? "/chat-sw" : "/chat");
        console.log(`🟢 Redirecting to: ${chatbotPage}`);
        navigate(chatbotPage, { replace: true });
      } else {
        console.error("❌ API Error:", data.error || "Unknown error");
        setError(data.error || text[language].errorMessage);
      }
    } catch (error) {
      console.error("❌ Network Error:", error);
      setError(text[language].networkError);
    } finally {
      setLoading(false);
    }
  };

  // Google Login
  const handleGoogleLogin = async (credentialResponse) => {
    setLoading(true);
    console.log("Google credential response:", credentialResponse);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: credentialResponse.credential, language }),
      });
      const data = await response.json();
      console.log("Backend response:", data);

      if (response.ok && data.token && data.name) {
        const firstName = data.name.includes(" ") ? data.name.split(" ")[0] : data.name;
        localStorage.setItem("token", data.token);
        localStorage.setItem("name", firstName);
        localStorage.setItem("language", language);
        const chatbotPage = data.redirect || (language === "sw" ? "/chat-sw" : "/chat");
        navigate(chatbotPage, { replace: true });
      } else {
        setError(data.error || text[language].errorMessage);
      }
    } catch (error) {
      console.error("Network Error Details:", error);
      setError(text[language].networkError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
    className="container-fluid d-flex flex-column align-items-center justify-content-center"
    style={{
      minHeight: "100vh",
      marginTop: "0",
      paddingTop: isMobile ? "80px" : "50px",
      background: "radial-gradient(circle at top, #eef2ff, #ffffff)",
    }}
  >
      {/* Logo & Title at Top Left */}
      <div
  className="position-absolute top-0 start-0 m-3 d-flex align-items-center"
  style={{ padding: "6px 10px" }}
>
        <img
          src="/umojai-logo.png"
          alt="Umojai Logo"
          style={{ width: "40px", height: "auto", maxHeight: "40px" }}
        />
        <h4
  className="fw-bold ms-2"
  style={{ color: "#2563eb", marginBottom: 0, letterSpacing: "-0.5px" }}
>
  Umojai
</h4>
      </div>

      {/* Top-right row: About + Language Switch (shrunk on mobile) */}
      <div
        className="position-absolute top-0 end-0 m-2"
        style={{
          display: "flex",
          whiteSpace: "nowrap",
          alignItems: "center",
          gap: isMobile ? "5px" : "15px",    // smaller gap on mobile
          fontSize: isMobile ? "12px" : "14px", // smaller text on mobile
        }}
      >
        {/* About Link */}
        <Link
          to="/about"
          className="text-decoration-none"
          style={{
            color: "#2563eb",
            marginRight: isMobile ? "5px" : "15px",
            fontWeight: 500,
          }}
        >
          About Umojai
        </Link>

        {/* English Button */}
        <button
          className={`btn btn-light mx-1 ${language === "en" ? "fw-bold" : ""}`}
          onClick={() => {
            setLanguage("en");
            localStorage.setItem("language", "en");
          }}
          style={{
            fontSize: isMobile ? "12px" : "14px",
            padding: isMobile ? "4px 8px" : "8px 14px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            backgroundColor: language === "en" ? "#f3f4f6" : "#ffffff",
          }}
        >
          English
        </button>

        {/* Kiswahili Button */}
        <button
          className={`btn btn-light mx-1 ${language === "sw" ? "fw-bold" : ""}`}
          onClick={() => {
            setLanguage("sw");
            localStorage.setItem("language", "sw");
          }}
          style={{
            fontSize: isMobile ? "12px" : "14px",
            padding: isMobile ? "4px 8px" : "8px 14px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            backgroundColor: language === "sw" ? "#f3f4f6" : "#ffffff",
          }}
        >
          Kiswahili
        </button>
      </div>

      <div
  style={{
    width: "100%",
    maxWidth: "480px",
    backgroundColor: "#ffffff",
    borderRadius: "18px",
    padding: isMobile ? "28px 20px" : "36px 32px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
  }}
>
  <h1
    className="fw-bold text-black text-center"
    style={{
      fontSize: isMobile ? "3rem" : "4.5rem",
      letterSpacing: "-1px",
      marginBottom: "10px",
    }}
  >
    {text[language].title}
  </h1>

  <p className="text-muted mb-4 text-center">{text[language].subtitle}</p>

  <div className="w-100 text-center mb-3 google-login-center">
    <GoogleLogin
      onSuccess={handleGoogleLogin}
      onError={() => setError(text[language].networkError)}
      text="continue_with"
      width={isMobile ? "100%" : "300px"}
      shape="rectangular"
    />
  </div>

  <div className="w-100 text-center mb-3">
    <span className="text-muted">OR</span>
  </div>

  <h2 className="text-center mb-3 fw-semibold" style={{ color: "#111827" }}>
    {text[language].loginButton}
  </h2>

  <div className="w-100">
  {error && (
  <p className="text-danger text-center" style={{ fontSize: "0.95rem", marginBottom: "12px" }}>
    {error}
  </p>
)}

    <form onSubmit={handleLogin} className="mt-3">
      <input
        type="email"
        className="form-control mb-3 w-100"
        placeholder={text[language].placeholderEmail}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        style={{ borderRadius: "10px", padding: "12px 14px" }}
      />
      <input
        type="password"
        className="form-control mb-3 w-100"
        placeholder={text[language].placeholderPassword}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        style={{ borderRadius: "10px", padding: "12px 14px" }}
      />
      <button
        type="submit"
        className="btn w-100"
        disabled={loading}
        style={{
          background: "linear-gradient(135deg, #2563eb, #4f46e5)",
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          padding: "12px 14px",
          fontWeight: 600,
        }}
      >
        {loading ? "Logging in..." : text[language].loginButton}
      </button>
    </form>

    <p className="text-center mt-3">
      <Link to={`/forgot-password?lang=${language}`}>{text[language].forgotPassword}</Link>
    </p>

    <p className="text-center mt-3">
      {text[language].signupText}{" "}
      <Link to={`/signup?lang=${language}`}>{text[language].signupLink}</Link>
    </p>
  </div>
</div>
</div>
  );
}
